import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Lightbulb, Loader2, Trash2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import BoardGridCanvas from '@/components/climbs/board-grid/BoardGridCanvas';
import CircuitList from '@/components/climbs/CircuitList';
import { CircuitOverlayCorner } from '@/components/climbs/CircuitOverlayButtons';
import { BoardHoldBar } from '@/components/climbs/climb-editor/BoardHoldBar';
import type { HoldHandControl } from '@/components/climbs/climb-editor/HoldHandButton';
import { MissingHint } from '@/components/climbs/climb-editor/MissingHint';
import {
  DRAWING_SURFACE_ATTRIBUTE,
  useClearSelectionOutside,
} from '@/components/climbs/climb-editor/useClearSelectionOutside';
import { PhotoOverlayButton } from '@/components/climbs/photo-editor/PhotoOverlayTools';
import { OVERLAY_ATTRIBUTE } from '@/components/climbs/board-grid/useZoomPan';
import { circuitHandIndex } from '@/components/climbs/circuit';
import {
  boardHoldRole,
  holdHands,
  otherHand,
  swapHand,
} from '@/components/climbs/board-grid/roles';
import { isShapedWall } from '@/components/climbs/board-grid/geometry';
import { cellName } from '@/components/climbs/board-grid/useCellName';
import { useBoardGrid } from '@/components/climbs/board-grid/useBoardGrid';
import {
  INCLINATION_STEPS,
  useBoardClimbDraft,
} from '@/components/climbs/board-grid/useBoardClimbDraft';
import type { Board } from '../../../types/device';
import type { Climb } from '../../../types/climb';
import type { BoardDashboardClimb } from '../../../types/board-dashboard';

interface BoardClimbFormPanelProps {
  gymSlug: string;
  board: Board;
  imageUrl: string;
  /**
   * The problem being corrected. Left out, the panel puts a new one up — the
   * two are one form because they are one job: painting holds on this wall and
   * saying what they add up to.
   */
  editing?: BoardDashboardClimb | null;
  /**
   * The dashboard's own drag handle, drawn between the two columns this panel
   * fills. Handed in rather than built here so the wall/form split is the same
   * one the wall/list split uses, and stays where the reader last put it.
   */
  separator: ReactNode;
  onCreated: (climb: Climb) => void;
  /** A correction was saved; the caller re-reads the list it may have moved in. */
  onEdited: () => void;
  /**
   * Takes the problem being corrected off the board. Rejects on failure, which
   * leaves the confirmation open so the setter can try again or back out.
   */
  onDelete?: () => Promise<void>;
  onCancel: () => void;
}

/**
 * Putting a problem up on the board's own page, or correcting one already on it.
 *
 * Takes over both columns of the dashboard for as long as it is open: the wall
 * on the left at the width the photo panel had, everything about the problem on
 * the right where the list of problems was. That is the trade — the list is one
 * click away and the wall is what the work is being done on, so it keeps the
 * room, while the fields, the tools and the run being built get a column of
 * their own instead of a strip under a photo.
 *
 * The problem itself — the holds, the roles, circuit mode, the live preview and
 * the save — is the same draft the member form at
 * `/gyms/:slug/boards/:id/climbs/new` uses. Only the controls differ: dropdowns
 * here, wheels on the phone.
 */
export function BoardClimbFormPanel({
  gymSlug,
  board,
  imageUrl,
  editing = null,
  separator,
  onCreated,
  onEdited,
  onDelete,
  onCancel,
}: BoardClimbFormPanelProps) {
  const { t } = useTranslation();
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const draft = useBoardClimbDraft({
    gymSlug,
    boardId: board.id,
    board,
    editing: editing && {
      id: editing.id,
      name: editing.name,
      description: editing.description,
      gradeId: editing.grade?.id ?? null,
      inclination: editing.inclination,
      circuit: editing.circuit,
      forcedHands: editing.forced_hands,
      holds: editing.holds,
    },
  });
  const { grid } = draft;

  // One board painted from two columns: the wall on the left is drawn by the
  // canvas, the row of controls on the right by the toolbar, and this is the
  // state they share.
  const painting = useBoardGrid({
    rows: grid.rows,
    columns: grid.columns,
    calibration: grid.calibration,
    holds: grid.boardHolds,
    extraHolds: grid.extraHolds,
    disabledCells: grid.disabledCells,
    shapes: grid.shapes,
    scanned: grid.scanned,
    holdRadii: grid.holdRadii,
    value: draft.holds,
    onChange: draft.setHolds,
    circuit: draft.circuit,
    focusedHold: draft.focusedHold,
    onFocusHold: draft.setFocusedHold,
    // A tap places the hold and picks it up; what it *is* is answered on the
    // bar along the bottom of the wall — or armed there first, as on the
    // member app's editor and the scans page. See BoardHoldBar.
    selectable: true,
  });

  // Pressing away from the wall puts the hold down: the setter has moved on to
  // the name or the grade, and the bar has nothing left to act on.
  useClearSelectionOutside(painting.clearSelection);

  /**
   * The hand the hold in hand is taken with, and the swap, for the bar — on a
   * circuit that forces hands. The same rule as the member app's editor: the
   * first real move flips the run from the start, anywhere else is a swap
   * against the alternation (see circuit.ts).
   */
  const handControl = ((): HoldHandControl | undefined => {
    if (!draft.circuit || !draft.forcedHands) return undefined;

    const at = painting.selectedIndex;
    const hold = at === null ? undefined : draft.holds[at];
    return {
      hands: hold ? holdHands(hold) : [],
      onSwap: () => {
        if (at === null || !hold) return;
        if (circuitHandIndex(draft.holds, boardHoldRole) === at) {
          return draft.setStartingHand(otherHand(hold.hand ?? 'left'));
        }
        draft.setHolds(swapHand(draft.holds, at));
      },
    };
  })();

  const confirmDelete = async () => {
    if (!onDelete) return;

    setDeleting(true);
    try {
      await onDelete();
      setConfirmingDelete(false);
    } catch {
      // The page has said what went wrong; the dialog stays for another go.
    } finally {
      setDeleting(false);
    }
  };

  /** Where a hold sits on the wall, for the rows of the circuit's list. */
  const holdName = (hold: { row: number; col: number }) =>
    cellName(t, hold, isShapedWall(grid.shapes));

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (editing) {
      if (await draft.saveEdit()) onEdited();
      return;
    }

    const created = await draft.save();
    if (created) onCreated(created);
  };

  return (
    <>
      {/* Left: the wall, scrolling on its own and nothing else on it — the
          photo is what the taps land on, so it gets the whole column. */}
      <div className="lg:min-h-0 lg:overflow-y-auto">
        <Card className="overflow-hidden">
          <CardContent className="p-0" {...{ [DRAWING_SURFACE_ATTRIBUTE]: '' }}>
            <BoardGridCanvas
              grid={painting}
              imageUrl={imageUrl}
              // Everything about the drawing is on the drawing, as on the scans
              // page: the circuit switch in its corner, the roles docked along
              // the bottom, what is still missing across the top and the lamp
              // top-right. The run itself is read back in the column opposite,
              // so there is nothing here to open.
              overlay={
                <>
                  <CircuitOverlayCorner
                    circuit={{ on: draft.circuit, onToggle: draft.setCircuit }}
                  />

                  <BoardHoldBar grid={painting} hand={handControl} />

                  <MissingHint
                    items={draft.missingRoles.map((role) => t(`climbs.holdRoles.${role}.label`))}
                  />

                  {/* Only a board that can actually drive its LEDs is offered
                      the preview — see can_light. A lamp that burns while it is
                      on, so a glance says whether the wall is following along. */}
                  {board.can_light && (
                    <div
                      className="absolute right-2 top-2 flex items-center gap-1"
                      {...{ [OVERLAY_ATTRIBUTE]: '' }}
                      onPointerDown={(event) => event.stopPropagation()}
                    >
                      <PhotoOverlayButton
                        label={t('climbs.board.livePreview')}
                        title={t('climbs.board.livePreviewHint')}
                        onClick={() => draft.setLivePreview((on) => !on)}
                        pressed={draft.livePreview}
                      >
                        <Lightbulb className="size-4" aria-hidden />
                      </PhotoOverlayButton>
                    </div>
                  )}
                </>
              }
            />
          </CardContent>
        </Card>
      </div>

      {separator}

      {/* Right: the problem, in the column the list of problems was in. From
          `lg` the card takes the height of that column and scrolls inside
          itself, which is what makes the buttons at the end a bar at the bottom
          of the screen rather than something at the end of a long form. Below
          `lg` the two columns are stacked and the page scrolls, so the bar
          sticks to the bottom of the viewport instead. */}
      <div className="lg:min-h-0">
        <Card className="lg:flex lg:h-full lg:flex-col lg:overflow-hidden">
          <CardContent className="p-0 lg:flex lg:min-h-0 lg:flex-1 lg:flex-col">
            <form onSubmit={submit} className="lg:flex lg:min-h-0 lg:flex-1 lg:flex-col">
              <div className="space-y-4 p-3 lg:min-h-0 lg:flex-1 lg:overflow-y-auto">
                {/* The way back to the list this column gave up, and — on a
                    problem already on the wall — the way to take it off. */}
                <div className="flex items-center justify-between gap-2">
                  <Button type="button" variant="ghost" size="sm" className="-ml-2" onClick={onCancel}>
                    <ArrowLeft className="mr-2 size-4" aria-hidden />
                    {t('gymAdmin.boardDashboard.backToProblems')}
                  </Button>

                  {editing && onDelete && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="text-destructive hover:text-destructive"
                      onClick={() => setConfirmingDelete(true)}
                    >
                      <Trash2 className="mr-2 size-4" aria-hidden />
                      {t('gymAdmin.boardDashboard.deleteProblem')}
                    </Button>
                  )}
                </div>

                {/* The run in the order it is climbed, at the head of the column
                    where it can be read against the wall opposite — the fourth
                    column of the scans page, on a board. Capped at half the
                    column and scrolling inside that, so a long circuit never
                    pushes the fields out of reach. */}
                {draft.circuit && (
                  <div
                    className="max-h-[50%] overflow-y-auto rounded-md border p-2"
                    data-testid="board-circuit-column"
                  >
                    <CircuitList
                      holds={draft.holds}
                      onChange={draft.setHolds}
                      roleOf={boardHoldRole}
                      nameOf={holdName}
                      selected={draft.focusedHold === null ? [] : [draft.focusedHold]}
                      onSelect={(index) =>
                        draft.setFocusedHold(draft.focusedHold === index ? null : index)
                      }
                      forcedHands={draft.forcedHands}
                      onForcedHandsChange={draft.setForcedHands}
                      onStartingHandChange={draft.setStartingHand}
                    />
                  </div>
                )}

                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="board-panel-name">{t('climbs.board.fields.name')}</Label>
                    <Input
                      id="board-panel-name"
                      value={draft.name}
                      onChange={(e) => draft.setName(e.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="board-panel-grade">{t('climbs.board.fields.grade')}</Label>
                    <Select value={draft.gradeId} onValueChange={draft.setGradeId}>
                      <SelectTrigger id="board-panel-grade">
                        <SelectValue placeholder={t('climbs.board.fields.gradePlaceholder')} />
                      </SelectTrigger>
                      <SelectContent>
                        {draft.grades.map((grade) => (
                          <SelectItem key={grade.id} value={grade.id}>
                            {grade.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* A board that cannot be re-angled still hangs at an angle,
                      but there is nothing here to pick: it grades the same
                      either way. Third in the same two-column grid, so it takes
                      the half under the name rather than squeezing the pair
                      above it. */}
                  {draft.adjustableInclination && (
                    <div className="space-y-2">
                      <Label htmlFor="board-panel-inclination">
                        {t('climbs.board.fields.inclination')}
                      </Label>
                      <Select
                        value={String(draft.inclination)}
                        onValueChange={(value) => draft.pickInclination(Number(value))}
                      >
                        <SelectTrigger id="board-panel-inclination">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {INCLINATION_STEPS.map((degrees) => (
                            <SelectItem key={degrees} value={String(degrees)}>
                              {degrees}°
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="board-panel-description">
                    {t('climbs.board.fields.description')}
                  </Label>
                  <Textarea
                    id="board-panel-description"
                    value={draft.description}
                    onChange={(e) => draft.setDescription(e.target.value)}
                    rows={2}
                  />
                </div>
              </div>

              {/* The way out and the way through, outside the scroll area so
                  they are on screen wherever the form has been scrolled to: a
                  setter who has just finished painting a circuit should not
                  have to go looking for the button that keeps the work. What a
                  dead Save is waiting for is written across the wall. */}
              <div className="sticky bottom-0 z-10 flex shrink-0 flex-wrap items-center justify-end gap-2 rounded-b-lg border-t bg-card p-3">
                <Button type="button" variant="outline" size="sm" onClick={onCancel}>
                  {t('common.cancel')}
                </Button>
                <Button type="submit" size="sm" disabled={!draft.canSave}>
                  {draft.saving && <Loader2 className="mr-2 size-4 animate-spin" />}
                  {t('common.save')}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>

      <AlertDialog
        open={confirmingDelete}
        onOpenChange={(open) => !deleting && setConfirmingDelete(open)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('gymAdmin.boardDashboard.deleteConfirmTitle', { name: editing?.name ?? '' })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('gymAdmin.boardDashboard.deleteConfirmDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              data-testid="board-problem-delete-confirm"
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={(event) => {
                // Held open until the server answers: a failure keeps it up.
                event.preventDefault();
                void confirmDelete();
              }}
            >
              {deleting && <Loader2 className="mr-2 size-4 animate-spin" />}
              {t('gymAdmin.boardDashboard.deleteProblem')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
